import * as React from 'react'
import { Col, Row } from 'reactstrap'
import { AccentButton, LongClickBtn, RTLGridRowDeleteAction } from '../controls/AccentButton';
import { AccentSpinner } from '../controls/AccentSpinner';
import { RTLGridLayout } from '../controls/RTLGridLayout';
import { CopyDocumentTemplateAsDlg } from '../dialogs/CopyDocumentTemplateAsDlg';
import { ProductPrintSettingsExportDlg } from '../dialogs/ProductPrintSettingsExportDlg';
import { ProductPrintSettingsImportDlg } from '../dialogs/ProductPrintSettingsImportDlg';
import AccentIcons from '../icons/Icons';
import { queryNoTracking, update } from '../services/DataService';
import { A, accentUtils, from, goToNew, showDialog, showOK, t } from '../services/HelperService';
import { getProducts } from '../services/ProductService';
import { documentTemplateTypes, printSettingTypes } from '../services/TemplateService';


const titleStyle = {
    color: "#212529b3"
}

// Installers list print settings in two kinds: HTML (the fitter sheet's layout) and PDF (product options only, for
// pdfscript install sheets). The chosen tab is remembered across visits.
const PRINT_SETTING_TAB_KEY = "documentTemplates.printSettingTab";
const printSettingTabs = { html: "html", pdf: "pdf" };

const readPrintSettingTab = () => {
    try {
        const saved = window.localStorage?.getItem(PRINT_SETTING_TAB_KEY);
        return saved === printSettingTabs.pdf ? printSettingTabs.pdf : printSettingTabs.html;
    } catch {
        return printSettingTabs.html;
    }
};

const writePrintSettingTab = (tab) => {
    try {
        window.localStorage?.setItem(PRINT_SETTING_TAB_KEY, tab);
    } catch {
        // storage unavailable (private mode / quota): the tab still switches for this visit
    }
};

const tabBarStyle = {
    display: 'flex',
    gap: '18px',
    borderBottom: '1px solid #dee2e6',
    margin: '0 0 8px 0'
};

const tabStyle = (active) => ({
    padding: '4px 2px 6px 2px',
    cursor: 'pointer',
    fontSize: '0.9em',
    color: active ? '#3b73af' : '#212529b3',
    borderBottom: active ? '2px solid #3b73af' : '2px solid transparent',
    marginBottom: '-1px',
    userSelect: 'none'
});

const tabCountStyle = {
    opacity: 0.6,
    fontSize: '0.85em',
    paddingLeft: '4px'
};

/** HTML / PDF tab bar for a print-settings list; `counts` shows how many settings each kind has. */
const PrintSettingTabBar = ({ tab, counts, onChange }) => {
    const tabs = [
        { id: printSettingTabs.html, text: "HTML", tagName: "printSettingTabHtml" },
        { id: printSettingTabs.pdf, text: "PDF", tagName: "printSettingTabPdf" },
    ];
    return <div style={tabBarStyle} role="tablist">
        {tabs.map(x => (
            <div
                key={x.id}
                role="tab"
                aria-selected={tab === x.id}
                data-tagname={x.tagName}
                style={tabStyle(tab === x.id)}
                onClick={() => onChange(x.id)}
            >
                {x.text}<span style={tabCountStyle}>({counts[x.id] ?? 0})</span>
            </div>
        ))}
    </div>;
};

const headerStyle = {
    backgroundColor: '#3b73af',
    textTransform: 'capitalize',
    color: 'white',
    textAlign: 'center',
    padding: '2px',
    margin: '7px 0px 7px 0px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
};

const buttonStyle = {
    backgroundColor: 'white',
    color: '#3b73af',
    border: 'none',
    borderRadius: '3px',
    padding: '2px 8px',
    fontSize: '0.8em',
    cursor: 'pointer',
    marginRight: '5px'
};

const sectionStyle = {
    marginBottom: '20px'
};

export const getDocumentTemplateType = (fieldName) => {
    if (!fieldName) return null;

    // Convert the input to lowercase for case-insensitive comparison
    const searchField = fieldName.toLowerCase();

    // Get all keys in the documentTemplateTypes object
    const keys = Object.keys(documentTemplateTypes);

    // Find the key that matches (case-insensitive)
    const matchedKey = keys.find(key => key.toLowerCase() === searchField);

    // Return the corresponding value or null if not found
    return matchedKey ? documentTemplateTypes[matchedKey] : null;
};



const query_DocumentTemplates = queryNoTracking("DocumentTemplates");
const query_PrintSettings = queryNoTracking("PrintSettings");

// One "Add" button by default; `actions` ([{ text, onClick, tagName }]) replaces it with several in the same style
// (the Installers print settings header offers "Add HTML" and "Add PDF").
const HeaderWithButton = ({ onClick, actions, children }) => {
    const buttons = Array.isArray(actions) && actions.length > 0 ? actions : [{ text: "Add", onClick }];
    return (
        <div style={headerStyle}>
            <span>{children}</span>
            <span>
                {buttons.map((a, i) => <button key={i} style={buttonStyle} data-tagname={a.tagName} onClick={a.onClick}>{a.text}</button>)}
            </span>
        </div>
    );
};

export default class DocumentTemplatesView extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            documentTemplates: null,
            printSettings: null,
            printSettingTab: readPrintSettingTab()
        };

        this.componentDidMount = this.componentDidMount.bind(this);
        this.refreshList = this.refreshList.bind(this);
        this.add = this.add.bind(this);
        this.addPrintSetting = this.addPrintSetting.bind(this);
        this.delete = this.delete.bind(this);
        this.deletePrintSetting = this.deletePrintSetting.bind(this);
        this.copy = this.copy.bind(this);
        this.copyPrintSetting = this.copyPrintSetting.bind(this);
        this.copyPrintSettingAsPdf = this.copyPrintSettingAsPdf.bind(this);
        this.setPrintSettingTab = this.setPrintSettingTab.bind(this);
        this.getItem = this.getItem.bind(this);
        this.getPrintSettingItem = this.getPrintSettingItem.bind(this);
        this.exportClicked = this.exportClicked.bind(this);
        this.importClicked = this.importClicked.bind(this);
    }

    componentDidMount() {
        this.refreshList();
    }

    refreshList() {
        query_DocumentTemplates.getAll().then(l => {
            this.setState({ documentTemplates: l });
        });

        query_PrintSettings.getAll().then(l => {
            this.setState({ printSettings: l });
        });
    }

    add(type) {
        goToNew("/DocumentTemplate", "DocumentTemplate", { Type: type });
    }

    addPrintSetting(type) {
        goToNew("/PrintSetting", "PrintSetting", { Type: type });        
    }

    delete(s) {
        update("DeleteDocumentTemplate", { IDs: [s.ID] }).then(r => this.refreshList());
    }

    copy(copyAs, s) {
        if (copyAs) {
            showDialog(<CopyDocumentTemplateAsDlg fromType={ s.Type} />).then(r => {
                if (!r.canceled && !accentUtils.isEmpty(r.type)) {
                    update("CopyDocumentTemplate", { ID: s.ID, Type: r.type }).then(r => {
                        this.refreshList();
                    });
                }
            });
        } else {
            update("CopyDocumentTemplate", { ID: s.ID, Type: s.Type }).then(r => {
                this.refreshList();
            });
        }
    }

    deletePrintSetting(s) {
        update("DeletePrintSettings", { IDs: [s.ID] }).then(r => this.refreshList());
    }

    copyPrintSetting(copyAs, s) {
        // copy-as stays within the HTML kinds; a PDF print setting (product options only) is copied verbatim
        if (copyAs && !printSettingTypes.isPdfScript(s.Type)) {
            showDialog(<CopyDocumentTemplateAsDlg fromType={s.Type} />).then(r => {
                if (!r.canceled && !accentUtils.isEmpty(r.type)) {
                    update("CopyPrintSettings", { ID: s.ID, Type: r.type }).then(r => {
                        this.refreshList();
                    });
                }
            });
        } else {
            update("CopyPrintSettings", { ID: s.ID, Type: s.Type }).then(r => {
                this.refreshList();
            });
        }
    }

    // The migration path: an HTML installer print setting copied as a PDF one (the server keeps the product options,
    // their order, labels and fabric flags and drops the HTML layout), then the list shows the PDF tab with the copy.
    copyPrintSettingAsPdf(s) {
        update("CopyPrintSettings", { ID: s.ID, Type: printSettingTypes.for(s.Type, true) }, true).then(r => {
            if (!accentUtils.isNull(r) && !r.IsFrameworkException) {
                this.setPrintSettingTab(printSettingTabs.pdf);
            }
            this.refreshList();
        });
    }

    setPrintSettingTab(tab) {
        writePrintSettingTab(tab);
        this.setState({ printSettingTab: tab });
    }

    exportClicked() {
        showDialog(<ProductPrintSettingsExportDlg documentTemplates={this.state.documentTemplates} printSettings={this.state.printSettings} products={getProducts()} />);
    }

    importClicked() {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json,application/json';

        input.onchange = e => {
            const file = e.target.files?.[0];

            if (accentUtils.isNull(file)) return;

            const reader = new FileReader();

            reader.onload = r => {
                try {
                    const exportFile = JSON.parse(r.target.result);
                    showDialog(<ProductPrintSettingsImportDlg exportFile={exportFile} documentTemplates={this.state.documentTemplates} printSettings={this.state.printSettings} products={getProducts()} />).then(res => {
                        if (!res.canceled) {
                            this.refreshList();
                        }
                    });
                } catch {
                    showOK("Import Failed", "The selected file is not a valid export JSON file.", ["application_strings.application.buttons.ok"], false, null, true, 600);
                }
            };

            reader.readAsText(file);
        };

        input.click();
    }


    getItem(r) {
        const moreStyle = {
            color: "#9d9fa0",
            cursor: "pointer"
        };

        return <div style={{ display: "flex" }}>
            <div className="p-2"><LongClickBtn style={moreStyle} onClick={e => this.copy(e, r)} icon={AccentIcons.Copy} /></div>
            <div className="p-2"><A href={`/DocumentTemplate/${r.ID}`}>{r.Name}</A></div>
            <RTLGridRowDeleteAction data={r} onDelete={e => this.delete(r)} top={ 7} />
        </div>;
    }

    getPrintSettingItem(r) {
        const moreStyle = {
            color: "#9d9fa0",
            cursor: "pointer"
        };

        // HTML installer settings offer "Copy as PDF" (the pdfscript kind has its own tab; nothing goes the other way)
        const canCopyAsPdf = printSettingTypes.for(r.Type, true) !== r.Type;

        return <div style={{ display: "flex" }}>
            <div className="p-2"><LongClickBtn style={moreStyle} onClick={e => this.copyPrintSetting(e, r)} icon={AccentIcons.Copy} /></div>
            {canCopyAsPdf && <div className="p-2"><AccentIcons.Pdf style={moreStyle} noPadding toolTipText="Copy as PDF print setting" onClick={e => this.copyPrintSettingAsPdf(r)} /></div>}
            <div className="p-2"><A href={`/PrintSetting/${r.ID}`}>{r.Name}</A></div>
            <RTLGridRowDeleteAction data={r} onDelete={e => this.deletePrintSetting(r)} top={7} />
        </div>;
    }

    render() {
        if (accentUtils.isNull(this.state.documentTemplates) || accentUtils.isNull(this.state.printSettings)) return <AccentSpinner />;

        const quotes = from(this.state.documentTemplates)
            .where(s => s.Type === documentTemplateTypes.quote)
            .orderBy(s => s.Name)
            .select(r => this.getItem(r)).toArray();

        const ocs = from(this.state.documentTemplates)
            .where(s => s.Type === documentTemplateTypes.orderConfirmation)
            .orderBy(s => s.Name)
            .select(r => this.getItem(r)).toArray();

        const invoices = from(this.state.documentTemplates)
            .where(s => s.Type === documentTemplateTypes.invoice)
            .orderBy(s => s.Name)
            .select(r => this.getItem(r)).toArray();

        const statements = from(this.state.documentTemplates)
            .where(s => s.Type === documentTemplateTypes.statement)
            .orderBy(s => s.Name)
            .select(r => this.getItem(r)).toArray();

        const installers = from(this.state.documentTemplates)
            .where(s => s.Type === documentTemplateTypes.installer)
            .orderBy(s => s.Name)
            .select(r => this.getItem(r)).toArray();

        const jobReviews = from(this.state.documentTemplates)
            .where(s => s.Type === documentTemplateTypes.jobReview)
            .orderBy(s => s.Name)
            .select(r => this.getItem(r)).toArray();


        const jss = from(this.state.documentTemplates)
            .where(s => s.Type === documentTemplateTypes.jobSummary)
            .orderBy(s => s.Name)
            .select(r => this.getItem(r)).toArray();

        const quotePrintSettings = from(this.state.printSettings)
            .where(s => s.Type === documentTemplateTypes.quote)
            .orderBy(s => s.Name)
            .select(r => this.getPrintSettingItem(r)).toArray();

        const ocPrintSettings = from(this.state.printSettings)
            .where(s => s.Type === documentTemplateTypes.orderConfirmation)
            .orderBy(s => s.Name)
            .select(r => this.getPrintSettingItem(r)).toArray();

        const invoicePrintSettings = from(this.state.printSettings)
            .where(s => s.Type === documentTemplateTypes.invoice)
            .orderBy(s => s.Name)
            .select(r => this.getPrintSettingItem(r)).toArray();

        const installerPrintSettings = from(this.state.printSettings)
            .where(s => s.Type === documentTemplateTypes.installer)
            .orderBy(s => s.Name)
            .select(r => this.getPrintSettingItem(r)).toArray();

        const installerPdfPrintSettings = from(this.state.printSettings)
            .where(s => s.Type === printSettingTypes.installerPdf)
            .orderBy(s => s.Name)
            .select(r => this.getPrintSettingItem(r)).toArray();

        const installerTab = this.state.printSettingTab;
        const installerTabCounts = { [printSettingTabs.html]: installerPrintSettings.length, [printSettingTabs.pdf]: installerPdfPrintSettings.length };
        const installerPrintSettingActions = [
            { text: "Add HTML", tagName: "addInstallerPrintSettingHtml", onClick: () => this.addPrintSetting(documentTemplateTypes.installer) },
            { text: "Add PDF", tagName: "addInstallerPrintSettingPdf", onClick: () => this.addPrintSetting(printSettingTypes.installerPdf) },
        ];

        // pdfscript-only: the templates live under Customizations; only their (light shape) print settings are managed here
        const supplyWorkOrderPrintSettings = from(this.state.printSettings)
            .where(s => s.Type === printSettingTypes.supplyWorkOrderPdf)
            .orderBy(s => s.Name)
            .select(r => this.getPrintSettingItem(r)).toArray();

        const jobReviewPrintSettings = from(this.state.printSettings)
            .where(s => s.Type === documentTemplateTypes.jobReview)
            .orderBy(s => s.Name)
            .select(r => this.getPrintSettingItem(r)).toArray();



        const jsPrintSettings = from(this.state.printSettings)
            .where(s => s.Type === documentTemplateTypes.jobSummary)
            .orderBy(s => s.Name)
            .select(r => this.getPrintSettingItem(r)).toArray();

        return (
            <Col md={12}>
                <Row>
                    <Col md={12}>
                        <div className="d-flex justify-content-end" style={{ gap: "5px", marginBottom: "10px" }}>
                            <AccentButton onClick={this.exportClicked}>Export</AccentButton>
                            <AccentButton onClick={this.importClicked}>Import</AccentButton>
                        </div>
                    </Col>
                </Row>
                {/* Quotes Section */}
                <div style={sectionStyle}>
                    <h4 style={titleStyle}>{t("application_strings.views.documentTemplateListView.quotes")}</h4>
                    <Row>
                        <Col md={6}>
                            <HeaderWithButton onClick={() => this.add(documentTemplateTypes.quote)}>
                                Templates
                            </HeaderWithButton>
                            <RTLGridLayout cols={4} items={quotes} />
                        </Col>
                        <Col md={6}>
                            <HeaderWithButton onClick={() => this.addPrintSetting(documentTemplateTypes.quote)}>
                                Print Settings
                            </HeaderWithButton>
                            <RTLGridLayout cols={4} items={quotePrintSettings} />
                        </Col>
                    </Row>
                </div>

                {/* Order Confirmations Section */}
                <div style={sectionStyle}>
                    <h4 style={titleStyle}>{t("application_strings.views.documentTemplateListView.orderConfirmations")}</h4>
                    <Row>
                        <Col md={6}>
                            <HeaderWithButton onClick={() => this.add(documentTemplateTypes.orderConfirmation)}>
                                Templates
                            </HeaderWithButton>
                            <RTLGridLayout cols={4} items={ocs} />
                        </Col>
                        <Col md={6}>
                            <HeaderWithButton onClick={() => this.addPrintSetting(documentTemplateTypes.orderConfirmation)}>
                                Print Settings
                            </HeaderWithButton>
                            <RTLGridLayout cols={4} items={ocPrintSettings} />
                        </Col>
                    </Row>
                </div>

                {/* Invoices Section */}
                <div style={sectionStyle}>
                    <h4 style={titleStyle}>{t("application_strings.views.documentTemplateListView.invoices")}</h4>
                    <Row>
                        <Col md={6}>
                            <HeaderWithButton onClick={() => this.add(documentTemplateTypes.invoice)}>
                                Templates
                            </HeaderWithButton>
                            <RTLGridLayout cols={4} items={invoices} />
                        </Col>
                        <Col md={6}>
                            <HeaderWithButton onClick={() => this.addPrintSetting(documentTemplateTypes.invoice)}>
                                Print Settings
                            </HeaderWithButton>
                            <RTLGridLayout cols={4} items={invoicePrintSettings} />
                        </Col>
                    </Row>
                </div>

                {/* Statements Section */}
                <div style={sectionStyle}>
                    <h4 style={titleStyle}>{t("application_strings.views.documentTemplateListView.statements")}</h4>
                    <Row>
                        <Col md={6}>
                            <HeaderWithButton onClick={() => this.add(documentTemplateTypes.statement)}>
                                Templates
                            </HeaderWithButton>
                            <RTLGridLayout cols={4} items={statements} />
                        </Col>
                        <Col md={6}>
                            {/* Empty column for consistency */}
                        </Col>
                    </Row>
                </div>

                {/* Installers Section */}
                <div style={sectionStyle}>
                    <h4 style={titleStyle}>{t("application_strings.views.documentTemplateListView.installers")}</h4>
                    <Row>
                        <Col md={6}>
                            <HeaderWithButton onClick={() => this.add(documentTemplateTypes.installer)}>
                                Templates
                            </HeaderWithButton>
                            <RTLGridLayout cols={4} items={installers} />
                        </Col>
                        <Col md={6}>
                            <HeaderWithButton actions={installerPrintSettingActions}>
                                Print Settings
                            </HeaderWithButton>
                            <PrintSettingTabBar tab={installerTab} counts={installerTabCounts} onChange={this.setPrintSettingTab} />
                            <RTLGridLayout cols={4} items={installerTab === printSettingTabs.pdf ? installerPdfPrintSettings : installerPrintSettings} />
                        </Col>
                    </Row>
                </div>

                {/* Supply Work Orders Section (pdfscript-only: templates are authored under Customizations) */}
                <div style={sectionStyle}>
                    <h4 style={titleStyle}>{t("application_strings.views.documentTemplateListView.supplyWorkOrders")}</h4>
                    <Row>
                        <Col md={6}>
                            <div style={{ color: "#718096", padding: "8px 0" }}>{t("application_strings.views.documentTemplateListView.supplyWorkOrderTemplatesNote")}</div>
                        </Col>
                        <Col md={6}>
                            <HeaderWithButton onClick={() => this.addPrintSetting(printSettingTypes.supplyWorkOrderPdf)}>
                                Print Settings
                            </HeaderWithButton>
                            <RTLGridLayout cols={4} items={supplyWorkOrderPrintSettings} />
                        </Col>
                    </Row>
                </div>

                {/* Job Review Section */}
                <div style={sectionStyle}>
                    <h4 style={titleStyle}>{t("application_strings.views.documentTemplateListView.jobReview")}</h4>
                    <Row>
                        <Col md={6}>
                            <HeaderWithButton onClick={() => this.add(documentTemplateTypes.jobReview)}>
                                Templates
                            </HeaderWithButton>
                            <RTLGridLayout cols={4} items={jobReviews} />
                        </Col>
                        <Col md={6}>
                            <HeaderWithButton onClick={() => this.addPrintSetting(documentTemplateTypes.jobReview)}>
                                Print Settings
                            </HeaderWithButton>
                            <RTLGridLayout cols={4} items={jobReviewPrintSettings} />
                        </Col>
                    </Row>
                </div>

                {/* Job Summaries Section */}
                <div style={sectionStyle}>
                    <h4 style={titleStyle}>{t("application_strings.views.documentTemplateListView.jobSummaries")}</h4>
                    <Row>
                        <Col md={6}>
                            <HeaderWithButton onClick={() => this.add(documentTemplateTypes.jobSummary)}>
                                Templates
                            </HeaderWithButton>
                            <RTLGridLayout cols={4} items={jss} />
                        </Col>
                        <Col md={6}>
                            <HeaderWithButton onClick={() => this.addPrintSetting(documentTemplateTypes.jobSummary)}>
                                Print Settings
                            </HeaderWithButton>
                            <RTLGridLayout cols={4} items={jsPrintSettings} />
                        </Col>
                    </Row>
                </div>
            </Col>
        );
    }
}